import clsx from 'clsx'

import {
  Button,
  Card,
  ChannelAvatar,
  Flex,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { getChannelName } from '~publish/helpers/channels'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'

import type { Service } from '../../graphqlEnums'

import styles from './ChannelRefreshCard.module.css'

/**
 * The channel fields the refresh flow and this card need. Structural rather
 * than a fragment type so both Community channel shapes satisfy it directly.
 */
export type RefreshableChannel = {
  id: string
  name: string
  displayName?: string | null
  avatar?: string | null
  service: Service
  apiVersion?: string | null
  isLocked?: boolean | null
}

export type ChannelRefreshCardProps = {
  channel: RefreshableChannel
  /** Identifies the entry point that started the refresh, for attribution. */
  cta: string
}

/**
 * A channel row with a Refresh button that starts the OAuth reconnect flow.
 * Shared by the Community refresh empty state and the reconnect-to-like dialog.
 */
export const ChannelRefreshCard = ({
  channel,
  cta,
}: ChannelRefreshCardProps): JSX.Element => {
  const refreshChannelConnection = useRefreshChannelConnection()
  const canManageChannels = useHasOrganizationAction('manageChannels')

  const handleRefreshChannel = (): void => {
    if (!canManageChannels) return
    refreshChannelConnection({ channel, cta })
  }

  const isLocked = channel.isLocked ?? false

  const refreshButton = (
    <Button
      variant="primary"
      size="large"
      aria-disabled={!canManageChannels}
      className={clsx(!canManageChannels && styles.permissionDisabled)}
      onClick={handleRefreshChannel}
    >
      Refresh
    </Button>
  )

  return (
    <Card
      padding="small"
      className={clsx(styles.base, isLocked && styles.locked)}
    >
      <Flex direction="row" gap="md" align="center">
        <ChannelAvatar
          src={channel.avatar ?? undefined}
          alt={channel.displayName || channel.name || 'Channel avatar'}
          channel={channel.service}
          size="mid"
          className={styles.avatar}
        />
        <Flex direction="column" className={styles.channelRowCenter}>
          <Text weight="bold" className={clsx(isLocked && styles.locked)}>
            {channel.name || channel.displayName}
          </Text>
          <Text
            color={isLocked ? 'disabled' : 'subtle'}
            className={styles.channelDescriptor}
          >
            {getChannelName(channel.service)}
          </Text>
        </Flex>
      </Flex>
      {canManageChannels ? (
        refreshButton
      ) : (
        <Tooltip content="Contact your admin to refresh this connection.">
          {refreshButton}
        </Tooltip>
      )}
    </Card>
  )
}
